// @flow

import * as React from 'react';
import styled from '@emotion/styled';
import { startsWith, get } from 'lodash/fp';

import Button from '../../components/commons/Button';
import Container from '../../components/layout/Container';
import RewriteLink from '../../components/routing/RewriteLink';
import Responsive from '../../components/responsive/Responsive';
import Rows from '../../components/layout/Rows';
import SportsFilterTab from '../../components/sportsFilters/SportsFilterTab';
import RapidFireLanding from './RapidFireLanding';

type Props = {
  competitions: Array<Object>,
  competitionId: number,
  contestType: string,
  defaultAssets: Object,
  entered5?: Object,
  entered3?: Object,
  entered3PayStructure?: Object,
  entered5PayStructure?: Object
};

const StyledContainer = styled(Container)({
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden'
});

const HeaderImage = styled.img({
  width: '100%'
});

const ContestTabGroup = styled.div({
  display: 'flex'
});

const ContestTab = styled(RewriteLink)(({ theme, selected, disabled }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  flexBasis: '50%',
  padding: theme.spacing.small,
  fontWeight: theme.fontWeight.normal,
  fontSize: theme.fontSize.small,
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? 'none' : 'inherit',
  color: selected ? theme.color.white : theme.color.basis2,
  backgroundColor: selected ? theme.color.primary : theme.color.white,
  '&:first-of-type': {
    borderRadius: '8px 0 0 8px'
  },
  '&:last-of-type': {
    borderRadius: '0 8px 8px 0'
  },
  '& > h3': {
    color: selected ? theme.color.white : theme.color.primary,
    fontSize: theme.fontSize.default,
    textTransform: 'uppercase',
    fontStyle: 'italic',
    fontWeight: theme.fontWeight.demiBold,
    margin: '0.5em'
  }
}));

const SubHeading = styled.div(({ theme }) => ({
  padding: theme.spacing.xsmall,
  paddingTop: theme.spacing.default
}));

const ButtonsWrapper = styled.div({
  display: 'flex',
  padding: '2em 0',
  '& > *': {
    flexBasis: '100%'
  }
});

const StyledSportsFilterTab = styled(SportsFilterTab)({
  margin: '5px 0'
});

class RapidFireLobby extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    entered5: undefined,
    entered3: undefined,
    entered3PayStructure: undefined,
    entered5PayStructure: undefined
  };

  render() {
    const {
      competitions,
      competitionId,
      contestType,
      defaultAssets,
      entered5,
      entered3,
      entered3PayStructure,
      entered5PayStructure
    } = this.props;
    const selectedCompetition = competitions.find(c => c.id === competitionId);
    let link = `/rapidfire/${competitionId}/${contestType}/enter`;

    if (contestType === '3' && entered3) {
      link = `/rapidfire/challenge/${entered3.id}`;
    } else if (contestType === '5' && entered5) {
      link = `/rapidfire/challenge/${entered5.id}`;
    }

    const cover = get('assets.cover', selectedCompetition) || defaultAssets.cover;
    const mobileCover = get('assets.mobileCover', selectedCompetition) || defaultAssets.mobileCover;

    return (
      <div>
        <Responsive desktop>
          {isDesktop => <HeaderImage src={isDesktop ? cover : mobileCover} />}
        </Responsive>
        <StyledContainer size="small">
          <SubHeading>Choose a competition</SubHeading>
          <Rows desktop={2} mobile={2}>
            {competitions.map(competition => (
              <StyledSportsFilterTab
                key={competition.id}
                active={competitionId === competition.id}
                disabled={!competition.active}
                href={`/rapidfire/${competition.id}/${contestType}/lobby`}
                icon={`icon-sport-${get('assets.icon', competition) || 'all'}`}
                image={
                  Array.isArray(competition.images)
                    ? competition.images.find(img => startsWith('http')(img))
                    : undefined
                }
              >
                {competition.name}
              </StyledSportsFilterTab>
            ))}
          </Rows>
          <SubHeading>Choose a contest type</SubHeading>
          <ContestTabGroup>
            <ContestTab selected={contestType === '3'} to={`/rapidfire/${competitionId}/3/lobby`}>
              <h3>Rapidfire 3 {entered3 ? '(Entered)' : ''}</h3>
              <span>3 out of 5 to win </span>
              <span>{entered3PayStructure ? entered3PayStructure.value : 0}x your entry fee</span>
            </ContestTab>
            <ContestTab selected={contestType === '5'} to={`/rapidfire/${competitionId}/5/lobby`}>
              <h3>Rapidfire 5 {entered5 ? '(Entered)' : ''}</h3>
              <span>5 out of 5 to win </span>
              <span>{entered5PayStructure ? entered5PayStructure.value : 0}x your entry fee</span>
            </ContestTab>
          </ContestTabGroup>
          <Responsive desktop>
            {isDesktop => (
              <ButtonsWrapper>
                <Button autoBorderRadius={isDesktop} kind="success" link={link}>
                  Continue
                </Button>
              </ButtonsWrapper>
            )}
          </Responsive>
        </StyledContainer>
        <RapidFireLanding
          hasCompetition={
            competitions.filter(item => item && item.rapidfire_enabled && item.rapidfire_enabled)
              .length > 0
          }
        />
      </div>
    );
  }
}

export default RapidFireLobby;
